import React, { useEffect, useState } from 'react';
import Breadcrumb from '../components/shared/Breadcrumb';
import { MdReceiptLong, MdDownload, MdArrowForward } from 'react-icons/md';
import { listMyInvoices, downloadInvoicePdf, Invoice, InvoiceStatus } from '../services/invoicesApi';
import { triggerBlobDownload } from '../utils/downloadBlob';
import { useNavigate } from 'react-router-dom';
import '../styles/InvoicesPage.css';

/**
 * VP-CUST-005 (ecommerce half): customer-facing invoices list + PDF download.
 *
 * This app previously had no invoices page/API-client calls at all — only
 * orders/deliveries. Backend endpoints (routes/invoices.ts):
 *   GET /customers/me/invoices  – this customer's own invoices
 *   GET /invoices/:id/pdf       – real PDF, ownership-scoped
 */

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: '#6B7280', bg: 'rgba(107,114,128,0.08)' },
  sent: { label: 'Sent', color: '#3B6EF6', bg: 'rgba(59,110,246,0.08)' },
  paid: { label: 'Paid', color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
  overdue: { label: 'Overdue', color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
  cancelled: { label: 'Cancelled', color: '#9CA3AF', bg: 'rgba(156,163,175,0.08)' },
};

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listMyInvoices({ limit: 50 })
      .then((res) => {
        if (!cancelled) setInvoices(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load invoices');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = async (invoice: Invoice) => {
    setDownloadError(null);
    setDownloadingId(invoice.id);
    try {
      const blob = await downloadInvoicePdf(invoice.id);
      triggerBlobDownload(blob, `${invoice.invoice_number}.pdf`);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="invoices-page">
      <Breadcrumb />

      <section className="invoices-page__header-section">
        <div className="invoices-page__header-inner">
          <div>
            <h1 className="invoices-page__title">My Invoices</h1>
            <p className="invoices-page__subtitle">{invoices.length} total invoices</p>
          </div>
        </div>
      </section>

      <section className="invoices-page__list-section">
        {downloadError && <p className="invoices-page__error-banner">{downloadError}</p>}

        {loading ? (
          <div className="invoices-page__empty">
            <h2>Loading your invoices…</h2>
          </div>
        ) : error ? (
          <div className="invoices-page__empty">
            <MdReceiptLong className="invoices-page__empty-icon" />
            <h2>Couldn't load invoices</h2>
            <p>{error}</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="invoices-page__empty">
            <MdReceiptLong className="invoices-page__empty-icon" />
            <h2>No invoices yet</h2>
            <p>Invoices are generated once an order has been processed.</p>
          </div>
        ) : (
          <div className="invoices-page__table-wrapper">
            <table className="invoices-page__table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const cfg = STATUS_CONFIG[inv.status] ?? STATUS_CONFIG.draft;
                  return (
                    <tr key={inv.id}>
                      <td className="invoices-page__num">{inv.invoice_number}</td>
                      <td>
                        <button
                          type="button"
                          className="invoices-page__order-link"
                          onClick={() => navigate(`/orders/${inv.order_id}`)}
                        >
                          VIN-{inv.order_id} <MdArrowForward />
                        </button>
                      </td>
                      <td>{new Date(inv.invoice_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td className="invoices-page__amount">${Number(inv.total_amount).toFixed(2)}</td>
                      <td>
                        <span className="invoices-page__status" style={{ color: cfg.color, background: cfg.bg }}>
                          {cfg.label}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="invoices-page__download-btn"
                          disabled={downloadingId === inv.id}
                          onClick={() => handleDownload(inv)}
                        >
                          <MdDownload /> {downloadingId === inv.id ? 'Downloading…' : 'PDF'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default InvoicesPage;
